<template>
  <div class="try-on-model-page">
    <h1>AI试衣模型</h1>
    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="success" class="alert alert-success">{{ success }}</div>

    <section class="model-list-section">
      <table class="data-table">
        <thead>
          <tr>
            <th>名称</th>
            <th>服务</th>
            <th>模型</th>
            <th>Endpoint</th>
            <th>API Key</th>
            <th>状态</th>
            <th>能力</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="model in models" :key="model.id">
            <td>{{ model.displayName }}</td>
            <td>{{ model.provider }}</td>
            <td>{{ model.modelCode }}</td>
            <td class="endpoint-cell">{{ model.endpoint }}</td>
            <td>{{ model.apiKeyMaskedHint || (model.hasApiKey ? '已配置' : '未配置') }}</td>
            <td>{{ model.enabled && model.status === 'ACTIVE' ? '启用' : '停用' }}</td>
            <td>{{ model.capabilitiesJson }}</td>
            <td class="actions">
              <button class="btn btn-secondary" type="button" @click="editModel(model)">编辑</button>
              <button class="btn btn-secondary" type="button" @click="verifyModel(model)">验证</button>
              <button
                :class="model.enabled && model.status === 'ACTIVE' ? 'btn btn-danger' : 'btn btn-success'"
                type="button"
                @click="toggleModel(model)"
              >
                {{ model.enabled && model.status === 'ACTIVE' ? '停用' : '启用' }}
              </button>
              <button class="btn btn-delete" type="button" @click="deleteModel(model)">删除</button>
            </td>
          </tr>
          <tr v-if="models.length === 0">
            <td colspan="8" class="empty">暂无模型，请先新增卖家试衣模型。</td>
          </tr>
        </tbody>
      </table>
    </section>

    <form class="model-form" @submit.prevent="saveModel">
      <h2>{{ form.id ? '编辑模型' : '新增模型' }}</h2>
      <div class="form-row">
        <label>模型服务</label>
        <select v-model="form.provider" @change="applyProviderDefaults">
          <option value="ALIYUN">阿里云 DashScope</option>
          <option value="HUNYUAN">腾讯混元</option>
          <option value="OPENAI_IMAGE">OpenAI Image</option>
        </select>
      </div>
      <div class="form-row">
        <label>显示名称</label>
        <input v-model="form.displayName" type="text" required>
      </div>
      <div class="form-row">
        <label>Endpoint</label>
        <input v-model="form.endpoint" type="url" required>
      </div>
      <div class="form-row">
        <label>模型编码</label>
        <input v-model="form.modelCode" type="text" required>
      </div>
      <div class="form-row">
        <label>API Key</label>
        <input v-model="form.apiKey" type="password" :placeholder="form._hasExistingKey ? '已配置Key，留空不修改' : '请输入API Key'">
      </div>
      <div class="form-row">
        <label>区域</label>
        <input v-model="form.region" type="text">
      </div>
      <div class="form-row">
        <label>超时秒数</label>
        <input v-model.number="form.timeoutSeconds" type="number" min="30">
      </div>
      <div class="form-row">
        <label>能力声明(JSON)</label>
        <textarea v-model="form.capabilitiesJson" rows="3"></textarea>
      </div>
      <div class="form-row">
        <label>扩展配置(JSON)</label>
        <textarea v-model="form.extraConfigJson" rows="3" placeholder='{"maskEnhancement":{"enabled":true,"featherRadius":5}}'></textarea>
        <p class="help-text">遮罩增强可在商品编辑页的可视化开关配置，此处用于查看或手动修改高级参数。</p>
      </div>
      <div class="form-row checkbox-row">
        <input id="enabled" v-model="form.enabled" type="checkbox">
        <label for="enabled">启用</label>
      </div>
      <div class="form-actions">
        <button class="btn" type="submit" :disabled="saving">{{ saving ? '保存中...' : '保存模型' }}</button>
        <button class="btn btn-secondary" type="button" @click="resetForm">重置</button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'TryOnModelManage',
  data() {
    return {
      models: [],
      error: '',
      success: '',
      saving: false,
      form: this.defaultForm()
    }
  },
  mounted() {
    this.loadModels()
  },
  methods: {
    defaultForm() {
      return {
        id: null,
        provider: 'ALIYUN',
        displayName: '阿里云试衣模型',
        region: 'cn-beijing',
        endpoint: 'https://dashscope.aliyuncs.com',
        modelCode: 'aitryon-plus',
        apiKey: '',
        authType: 'BEARER',
        timeoutSeconds: 120,
        capabilitiesJson: '["TRY_ON","ASYNC_TASK"]',
        extraConfigJson: '',
        enabled: true
      }
    },
    applyProviderDefaults() {
      if (this.form.provider === 'HUNYUAN') {
        this.form.displayName = '腾讯混元试衣模型'
        this.form.region = 'ap-guangzhou'
        this.form.endpoint = 'https://tokenhub.tencentmaas.com/v1/api/image'
        this.form.modelCode = 'hy-image-v3.0'
        this.form.capabilitiesJson = '["TRY_ON","ASYNC_TASK"]'
      } else if (this.form.provider === 'OPENAI_IMAGE') {
        this.form.displayName = 'OpenAI 图像试衣模型'
        this.form.region = 'global'
        this.form.endpoint = 'https://api.openai.com/v1/images/edits'
        this.form.modelCode = 'gpt-image-1.5'
        this.form.capabilitiesJson = '["TRY_ON","MASK_EDIT","INPAINT","MULTI_IMAGE_INPUT","CUSTOM_PROMPT"]'
      } else {
        this.form.displayName = '阿里云试衣模型'
        this.form.region = 'cn-beijing'
        this.form.endpoint = 'https://dashscope.aliyuncs.com'
        this.form.modelCode = 'aitryon-plus'
        this.form.capabilitiesJson = '["TRY_ON","ASYNC_TASK"]'
      }
    },
    async loadModels() {
      try {
        const response = await this.$axios.get('/seller/try-on/models')
        this.models = Array.isArray(response.data) ? response.data : []
      } catch (err) {
        this.error = err.response?.data?.message || '获取模型配置失败'
      }
    },
    editModel(model) {
      this.form = Object.assign(this.defaultForm(), model)
      this.form._hasExistingKey = model.hasApiKey === true
    },
    async saveModel() {
      try {
        this.saving = true
        this.error = ''
        const payload = Object.assign({}, this.form)
        const url = payload.id ? `/seller/try-on/models/${payload.id}` : '/seller/try-on/models'
        const method = payload.id ? 'put' : 'post'
        const response = await this.$axios[method](url, payload)
        this.success = '模型配置已保存'
        this.form = Object.assign(this.defaultForm(), response.data, { apiKey: '' })
        await this.loadModels()
      } catch (err) {
        this.error = err.response?.data?.message || '保存模型配置失败'
      } finally {
        this.saving = false
      }
    },
    async verifyModel(model) {
      try {
        await this.$axios.post(`/seller/try-on/models/${model.id}/verify`)
        this.success = '模型配置验证完成'
        await this.loadModels()
      } catch (err) {
        this.error = err.response?.data?.message || '验证模型失败'
      }
    },
    async toggleModel(model) {
      try {
        if (model.enabled && model.status === 'ACTIVE') {
          await this.$axios.delete(`/seller/try-on/models/${model.id}`)
          this.success = '模型已停用'
        } else {
          await this.$axios.put(`/seller/try-on/models/${model.id}`, Object.assign({}, model, { enabled: true }))
          this.success = '模型已启用'
        }
        await this.loadModels()
      } catch (err) {
        this.error = err.response?.data?.message || '操作失败'
      }
    },
    async deleteModel(model) {
      if (!confirm(`确定要永久删除模型「${model.displayName}」吗？此操作不可恢复。`)) return
      try {
        await this.$axios.delete(`/seller/try-on/models/${model.id}/permanent`)
        this.success = '模型已删除'
        await this.loadModels()
      } catch (err) {
        this.error = err.response?.data?.message || '删除模型失败'
      }
    },
    resetForm() {
      const currentProvider = this.form.provider
      this.form = this.defaultForm()
      this.form.provider = currentProvider
      this.applyProviderDefaults()
      this.error = ''
      this.success = ''
    }
  }
}
</script>

<style scoped>
.try-on-model-page {
  padding: 20px;
}

.model-list-section {
  margin-bottom: 24px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
}

.data-table th,
.data-table td {
  border-bottom: 1px solid #ddd;
  padding: 10px;
  text-align: left;
  vertical-align: top;
}

.endpoint-cell {
  max-width: 260px;
  word-break: break-all;
}

.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.empty {
  text-align: center;
  color: #666;
}

.model-form {
  border: 1px solid #ddd;
  padding: 16px;
  border-radius: 4px;
}

.form-row {
  margin-bottom: 12px;
}

.form-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.help-text {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #888;
}

.form-row input,
.form-row select,
.form-row textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-row input {
  width: auto;
}

.checkbox-row label {
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  color: white;
  background: #E02E24;
  cursor: pointer;
}

.btn-secondary {
  background: #666;
}

.btn-danger {
  background: #b42318;
}

.btn-success {
  background: #2e7d32;
}

.btn-delete {
  background: #9e9e9e;
  color: white;
}

.alert {
  padding: 10px;
  margin-bottom: 12px;
  border-radius: 4px;
}

.alert-danger {
  color: #721c24;
  background: #f8d7da;
}

.alert-success {
  color: #155724;
  background: #d4edda;
}
</style>
